import React from 'react';
import { CheckSquare } from 'lucide-react';
import type { BlueprintChecklistItem } from '../../../types/processBlueprint';

interface MinhaMesaChecklistProps {
  items: BlueprintChecklistItem[];
  checklistState: Record<string, boolean>;
  onToggle: (itemId: string) => void;
  readOnly?: boolean;
}

export const MinhaMesaChecklist: React.FC<MinhaMesaChecklistProps> = ({
  items,
  checklistState,
  onToggle,
  readOnly = false,
}) => {
  if (items.length === 0) {
    return null;
  }

  const checkedCount = items.filter((item) => checklistState[item.id]).length;
  const progressPercent = items.length > 0 ? Math.round((checkedCount / items.length) * 100) : 0;
  const requiredCount = items.filter((item) => item.required).length;
  const requiredChecked = items.filter((item) => item.required && checklistState[item.id]).length;
  const allRequiredDone = requiredCount === 0 || requiredChecked === requiredCount;

  return (
    <div className="space-y-2">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 overflow-hidden border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
          <div
            className={`h-full transition-all duration-300 ${allRequiredDone ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-amber-500 dark:bg-amber-400'}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="font-mono text-[10px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap tabular-nums">
          {checkedCount}/{items.length}
        </span>
      </div>

      {/* Required blocking indicator */}
      {!allRequiredDone && (
        <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">
          {requiredCount - requiredChecked} item{requiredCount - requiredChecked > 1 ? 'ns' : ''} obrigatorio{requiredCount - requiredChecked > 1 ? 's' : ''} pendente{requiredCount - requiredChecked > 1 ? 's' : ''} — conclusao bloqueada
        </p>
      )}

      {/* Items */}
      <ul className="space-y-1">
        {items.map((item) => {
          const checked = checklistState[item.id] ?? false;
          return (
            <li key={item.id} className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={checked}
                disabled={readOnly}
                onChange={() => onToggle(item.id)}
                className="mt-0.5 h-3.5 w-3.5 rounded-sm border-slate-300 dark:border-slate-600 text-slate-700 focus:ring-slate-500 dark:bg-slate-800"
              />
              <span
                className={`text-[11px] ${
                  checked
                    ? 'text-slate-400 dark:text-slate-500 line-through'
                    : 'text-slate-700 dark:text-slate-300'
                }`}
              >
                {item.label}
                {item.required && !checked && (
                  <span className="ml-1 font-mono text-[9px] text-amber-600 dark:text-amber-400">*</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
